import Style from './container.module.scss';
import { useNode, useEditor } from '@craftjs/core';
import {
  FormRepeatableContainerComponent,
  FormRepeatableSection,
} from 'src/features/generalForms/builder/types/RepeatableContainer';
import { AppButton } from 'uikit/buttons';
import { useFormContext } from 'react-hook-form';
import { getCloneTree } from 'src/components/formEditor/uiEditor/uiEditor-helper';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ReactComponent as PlusIcon } from 'src/assets/img/records/add_24.svg';
import { ReactComponent as CrossIcon } from 'src/assets/img/generalBuilder/cross_20.svg';
import { createContext, useContextSelector } from 'use-context-selector';
import { useFieldState } from 'src/features/generalForms/builder/layout/panels/helpers/expression/useFieldState';
import { createId } from 'src/helpers/typeUtils';
import { generateNestedDataKey } from 'src/features/generalForms/builder/layout/panels/helpers/helper';

export const RepeatableContainer: FormRepeatableContainerComponent = ({
  dataKey: rootDataKey,
  children,
  addButtonText,
  title,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { actions, query } = useEditor();
  const { node } = useNode((n) => ({
    styles: n.data.custom,
    node: n,
  }));

  const form = useFormContext();
  const { isDisabled, visible } = useFieldState(form);

  const addSection = useCallback(
    (sectionDataKey: string) => {
      const newTree = getCloneTree(query, node.data.linkedNodes.id, generateNestedDataKey(query, sectionDataKey));
      actions.addNodeTree(newTree, node.id);
    },
    [actions, node.data.linkedNodes.id, node.id, query],
  );

  // Only for the first render
  useEffect(() => {
    // IMPORTANT: There will be a slight delay to allow Craft.js to load ROOT.
    const timer = setTimeout(() => {
      form.getValues(`${rootDataKey!}.order`)?.forEach(addSection);
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addNewSectionHandler = useCallback(() => {
    const order = form.getValues(`${rootDataKey}.order`) ?? [];
    const sectionDataKey = createId();

    addSection(sectionDataKey);
    form.setValue(`${rootDataKey}.order`, [...order, sectionDataKey]);
    // IMPORTANT: There will be a slight delay to allow Craft.js to load ROOT.
    setTimeout(() => buttonRef.current?.scrollIntoView(true), 0);
  }, [addSection, form, rootDataKey]);

  const removeHandler = useCallback(
    (sectionDataKey: string, nodeId: string) => {
      const order = form.getValues(`${rootDataKey!}.order`);
      const newOrder = order?.filter((x: string) => x !== sectionDataKey) as number[] | undefined;

      if (!newOrder?.length) form.unregister(`${rootDataKey}.order`);
      else form.setValue(`${rootDataKey}.order`, newOrder);

      form.unregister(`${rootDataKey}.${sectionDataKey}`);
      actions.delete(nodeId);
    },
    [actions, form, rootDataKey],
  );

  // Remove sections if container is disabled or not visible
  useEffect(() => {
    if (isDisabled || !visible) {
      query
        .node(node.id)
        .descendants(false, 'childNodes')
        .map((nodeId) => query.node(nodeId).get())
        .map((n) => ({ sectionDataKey: n.data.props.dataKey, nodeId: n.id }))
        .forEach((x) => removeHandler(x.sectionDataKey, x.nodeId));
    }
  }, [isDisabled, node.id, query, removeHandler, visible]);

  if (!visible) return <></>;

  return (
    <div>
      {title && (
        <div aria-disabled={form.formState.isSubmitting} className={Style.title}>
          {title}
        </div>
      )}
      <FieldArrayContext.Provider value={{ remove: removeHandler }}>{children}</FieldArrayContext.Provider>
      <AppButton
        ref={buttonRef}
        className={Style.addButton}
        colorSchema={'primary'}
        variant={'icon-link'}
        Icon={PlusIcon}
        text={addButtonText}
        onClick={addNewSectionHandler}
        disabled={isDisabled || form.formState.isSubmitting}
      />
    </div>
  );
};

export const RepeatableSection: FormRepeatableSection = ({ children, dataKey, sectionTitle }) => {
  const { query } = useEditor();
  const { remove } = useContextSelector(FieldArrayContext, (x) => x);
  const { id, parentNodeId, styles } = useNode((n) => ({
    parentNodeId: n.data.parent,
    styles: n.data.custom,
  }));
  const { formState } = useFormContext();
  const deleteSectionHandler = useCallback(() => {
    remove(dataKey!, id);
  }, [dataKey, id, remove]);

  const sectionIndex = useMemo(
    () => query.node(parentNodeId).descendants(false, 'childNodes').indexOf(id) + 1,
    [id, parentNodeId, query],
  );

  return (
    <div className={Style.repeatableSection}>
      <div aria-disabled={formState.isSubmitting} className={Style.header}>
        {sectionTitle && <div className={Style.sectionTitle}>{`${sectionTitle} ${sectionIndex}`}</div>}
        <AppButton
          className={Style.removeButton}
          variant={'icon-link'}
          colorSchema={'basic'}
          Icon={CrossIcon}
          onClick={deleteSectionHandler}
          disabled={formState.isSubmitting}
        />
      </div>
      <div className={Style.container} style={styles}>
        {children}
      </div>
    </div>
  );
};

const FieldArrayContext = createContext({
  remove: (sectionDataKey: string, nodeId: string) => {},
});
